<template>
	<div class="swap">
		<div class="row small" v-show="!hideSettings">
			<div class="col">
				<button
					class="btn btn-white refresh-btn border-0"
					aria-label="Refresh"
					@click="onRefresh"
				>
					<i class="fa fa-refresh"></i>
				</button>
			</div>
			<div class="col-auto text-end">
				<router-link
					to="/app/settings"
					class="settings-link btn btn-white border-0"
				><i class="fa fa-cogs"></i>
					<span title="Slippage" class="ms-1"
					>{{ $store.state.settings.slippage }}%</span
					></router-link
				>
			</div>
		</div>
		<div class="card animate__animated animate__fadeIn mt-1">
			<div class="card-header"></div>
			<div class="card-body">
				<DualSelect
					@update="onDualSelectUpdate"
					@inputA="onInputA"
					:token-b-amount="optimalRouteOutput"
				>
					<template v-slot:header-a
					><h4 class="mb-0 d-none d-md-inline-block">
						You're paying
					</h4></template
					>
					<template v-slot:header-b>To receive</template>
				</DualSelect>
			</div>

			<div class="card-footer">
				<button
					@click="doSwap"
					:disabled="!canSwap || routesLoading"
					class="btn btn-solid-primary w-100 mt-2"
				>
          <span
						:class="{ 'fa-beat-fade': loading || routesLoading }"
						style="--fa-beat-fade-opacity: 0.67; --fa-beat-fade-scale: 1.075"
					>
            <i
							class="fa fa-circle-notch fa-spin"
							v-show="loading || routesLoading"
						></i>
            {{ btnText }}
          </span>
				</button>

				<div v-if="amountIn > 0" class="alerts mt-3">
					<div
						v-if="
              !routesLoading &&
              !useJupRoute &&
              poolTotalLiquidity &&
              poolTotalLiquidity < 3000
            "
						class="alert alert-warning small p-1 text-center"
					>
						<i class="small"
						>This pool has very low liquidity (${{
								poolTotalLiquidity.toFixed(2)
							}}). This will result in high slippage when swapping, trade with
							caution</i
						>
					</div>
					<div
						v-if="freezeAuthorityDetected"
						class="alert alert-danger small p-1 text-center"
					>
						<i class="small"
						>A token in this pool has Freeze Authority enabled! The mint owner
							can freeze your tokens, trade with caution</i
						>
					</div>
					<div
						v-if="mintAuthorityDetected"
						class="alert alert-danger small p-1 text-center"
					>
						<i class="small"
						>A token in this pool has Mint Authority enabled! The mint owner
							may mint new tokens at any time, trade with caution</i
						>
					</div>
				</div>
			</div>
		</div>

		<div v-if="!hideInfo" class="my-2">
			<div class="">
				<div v-if="optimalRouteOutput > 0" class="row">
					<RouteContainer
						provider="FluxBeam"
						class="col-12 my-2"
						:class="{
              'order-0': !useJupRoute || routesLoading,
              'order-1': useJupRoute,
            }"
						:active="!poolLoading && !useJupRoute"
						v-if="poolDetail"
						expanded
					>
						<table class="table table-sm table-borderless table-no-hover xsmall">
							<tbody>
							<tr>
								<td>Minimum Received</td>
								<td class="text-end">
									{{ minimumOutFluxHu }} {{ tokenBSymbol }}
								</td>
							</tr>
							<tr>
								<td>Price Impact</td>
								<td class="text-end" :class="{'red fw-bold': priceImpact>30}">{{ priceImpact.toFixed(2) }}%</td>
							</tr>
							<tr>
								<td>Transaction Fee</td>
								<td class="text-end">
									{{ transactionFee.toPrecision(9) }} SOL
								</td>
							</tr>
							<tr v-if="mintASwapFee > 0 || mintBSwapFee > 0">
								<td>Token Fee</td>
								<td class="text-end">
                    <span v-if="mintASwapFee > 0"
										>{{ mintASwapFee.toLocaleString() }}
                      {{ aToB ? tokenASymbol : tokenBSymbol }}</span
										>
									<span
										v-if="mintASwapFee > 0 && mintBSwapFee > 0"
										class="mx-2"
									>|</span
									>
									<span v-if="mintBSwapFee > 0"
									>{{ mintBSwapFee.toLocaleString() }}
                      {{ aToB ? tokenBSymbol : tokenASymbol }}</span
									>
								</td>
							</tr>

							<tr>
								<td>
									Pool liquidity ({{ aToB ? tokenASymbol : tokenBSymbol }})
								</td>
								<td class="text-end">
									{{ poolDetail?.tokenAccountA?.tokenAmount.uiAmount }}
									{{ aToB ? tokenASymbol : tokenBSymbol }}
								</td>
							</tr>
							<tr>
								<td>
									Pool liquidity ({{ aToB ? tokenBSymbol : tokenASymbol }})
								</td>
								<td class="text-end">
									{{ poolDetail?.tokenAccountB?.tokenAmount.uiAmount }}
									{{ aToB ? tokenBSymbol : tokenASymbol }}
								</td>
							</tr>
							</tbody>
						</table>
					</RouteContainer>

					<RouteContainer
						provider="Jupiter"
						class="col-12 my-2"
						:class="{ 'order-0': useJupRoute, 'order-1': !useJupRoute }"
						:active="!jupLoading && useJupRoute"
						v-if="!isToken22 || !this.poolDetail"
						expanded
					>
						<JupiterRoute
							:route="jupRoute"
							:token-a="tokenA"
							:token-b="tokenB"
						></JupiterRoute>
					</RouteContainer>
				</div>
			</div>

			<div class="row animate__animated animate__fadeIn order-3 mt-4" v-if="tokenA.address">
				<div class="col-12 col-md-6 text-center">
					<TokenImage class="me-2 align-middle" :mint="tokenA.address" :key="tokenA.address.toString()"></TokenImage>
					<TokenPrice class="ml-2 font-bold" :token="tokenA.address"></TokenPrice>
					<div>
						<AccountAddress :names="false" :token="true" :address="tokenA.address"></AccountAddress>
					</div>
					<div>
						<RugWidget :mint="tokenA.address" class="mt-2"></RugWidget>
					</div>
				</div>
				<div class="col-12 col-md-6 text-center mt-3 mt-md-0" v-if="tokenB.address">
					<TokenImage class="me-2 align-middle" :mint="tokenB.address" :key="tokenB.address.toString()"></TokenImage>
					<TokenPrice class="ml-2 font-bold" :token="tokenB.address"></TokenPrice>
					<div>
						<AccountAddress :names="false" :token="true" :address="tokenB.address"></AccountAddress>
					</div>
					<div>
						<RugWidget :mint="tokenB.address" class="mt-2"></RugWidget>
					</div>
				</div>
			</div>
		</div>

		<portal to="body">
			<PoolWarning
				v-show="showRisk"
				@accept="doSwapRiskAccept"
				@close="showRisk = false"
				:liquidity="poolTotalLiquidity"
				:price-impact="priceImpact"
			></PoolWarning>
		</portal>
	</div>
</template>

<script>
import {TokenInput} from "@/api/token_swap/layouts";
import {JupiterClient} from "@/api/jupiter/client";
import {BN, web3} from "@project-serum/anchor";
import DualSelect from "@/components/token/DualSelect.vue";
import {parseCustomError} from "@/api/token_swap/errors";
import TokenPrice from "@/components/common/TokenPrice.vue";
import TokenImage from "@/components/common/TokenImage.vue";
import SignatureLoader from "@/mixins/SignatureLoader";
import PoolWarning from "@/components/common/PoolWarning";
import AccountAddress from "@/components/common/AccountAddress.vue";
import JupiterRoute from "@/components/swap/JupiterRoute.vue";
import RouteContainer from "@/components/swap/RouteContainer.vue";
import RugWidget from "@/components/rugcheck/RugWidget.vue";
import store from "@/store";
import {createFluxBotSwap} from "@/api/endpoints";
import {mapStores} from "pinia";
import {useSessionStore, useWalletStore} from "@/pinia";

export default {
	name: "Swap",
	components: {
		RugWidget,
		RouteContainer,
		JupiterRoute,
		AccountAddress,
		TokenImage,
		TokenPrice,
		DualSelect,
		PoolWarning,
	},
	mixins: [SignatureLoader],
	props: {
		hideSettings: {
			type: Boolean,
		},
		hideInfo: {
			type: Boolean,
		},
	},
	data() {
		return {
			jupClient: null,
			showRisk: false,
			acceptedRisk: false,
			interval: null,
			loading: false,
			routes: null,
			jupRoute: null,
			poolDetail: null,
			timeout: null, //Debounce timeout#
			poolLoading: false,
			jupLoading: false,
		};
	},
	computed: {
		...mapStores(useWalletStore),
		amountIn: function () {
			return this.$store.state.swap.tokenAAmount;
		},
		tokenA: function () {
			return this.$store.state.swap.tokenA;
		},
		tokenB: function () {
			return this.$store.state.swap.tokenB;
		},

		tokenABalance: function () {
			return this.$store.state.cache.userTokens[this.tokenA.address]?.account
				.data.parsed.info.tokenAmount.amount;
		},

		tokenBBalance: function () {
			return this.$store.state.cache.userTokens[this.tokenB.address]?.account
				.data.parsed.info.tokenAmount.amount;
		},

		routesLoading: function () {
			return !this.poolDetail && this.jupLoading;
		},

		useJupRoute: function () {
			if (!this.jupRoute)
				return false

			return this.poolTotalLiquidity < 1000 || this.jupRoute?.otherAmountThreshold > this.minimumOut;
		},

		tokenASymbol: function () {
			return this.tokenA?.symbol?.toUpperCase().trim().replace(/\0/g, "");
		},
		tokenBSymbol: function () {
			return this.tokenB?.symbol?.toUpperCase().trim().replace(/\0/g, "");
		},

		isToken22: function () {
			if (this.poolDetail) {
				return (
					this.poolDetail.mintProgramA.toString() ===
					"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb" ||
					this.poolDetail.mintProgramB.toString() ===
					"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
				);
			}

			return (
				this?.tokenA?.programID ===
				"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb" ||
				this?.tokenB?.programID ===
				"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
			);
		},

		canSwap: function () {
			if (this.useJupRoute) return true;

			if (!this.poolAvailable || this.loading || !this.poolDetail) return false;

			//TODO do we still need this?
			// let bTotal = this.poolDetail.tokenAccountB?.tokenAmount.amount
			// if (this.poolDetail.tokenAccountA.mint !== this.tokenA.address) {
			// 	bTotal = this.poolDetail.tokenAccountA?.tokenAmount.amount
			// }
			//
			// if (Number(bTotal) <= Number(this.form.tokenB.amount))
			// 	return false

			return true;
		},
		btnText: function () {
			if (this.routesLoading) return "Refreshing routes";

			if (Number(this.amountIn) <= 0) return "Enter amount";

			if (this.useJupRoute) {
				return "Swap";
			}

			if (!this.poolAvailable) return "No pool found";

			// if (this.tokenABalance > 0 && this.tokenABalance < Number(this.amountIn)) {
			// 	return `Insufficient balance`
			// }

			if (!this.poolDetail) {
				return `No pool found`;
			}

			// let bTotal = this.poolDetail.tokenAccountB.tokenAmount.amount
			// if (this.poolDetail.tokenAccountA.mint !== this.tokenA.address) {
			// 	bTotal = this.poolDetail.tokenAccountA.tokenAmount.amount
			// }

			return "Swap";
		},
		poolTotalLiquidity: function () {
			if (!this.poolDetail) return 0;

			const ta =
				this.$store.state.cache.tokenPrices[this.poolDetail.tokenAccountA?.mint]
					?.price || 0;
			const tb =
				this.$store.state.cache.tokenPrices[this.poolDetail.tokenAccountB?.mint]
					?.price || 0;

			const pa = this.poolDetail.tokenAccountA?.tokenAmount.uiAmount * ta;
			const pb = this.poolDetail.tokenAccountB?.tokenAmount.uiAmount * tb;
			return pa + pb;
		},
		mintATransferFee: function () {
			const tFee = this.poolDetail?.mintA?.extensions?.filter(
				(ext) => ext.extension === "transferFeeConfig"
			);
			if (!tFee || !tFee.length) return null;

			return tFee[0]?.state?.newerTransferFee;
		},
		mintBTransferFee: function () {
			const tFee = this.poolDetail?.mintB?.extensions?.filter(
				(ext) => ext.extension === "transferFeeConfig"
			);
			if (!tFee || !tFee.length) return null;

			return tFee[0]?.state?.newerTransferFee;
		},
		mintASwapFee: function () {
			if (!this.mintATransferFee) return 0;

			const feeRate =
				this.mintATransferFee?.transferFeeBasisPoints / 10_000 || 0;
			const input = this.aToB ? this?.tokenA : this?.tokenB;

			const inputAmount = Number(input.amount);
			return (
				Math.min(this.mintATransferFee?.maximumFee, inputAmount * feeRate) /
				Math.pow(10, input.decimals)
			);
		},
		mintBSwapFee: function () {
			if (!this.mintBTransferFee) return 0;

			const feeRate =
				this.mintBTransferFee?.transferFeeBasisPoints / 10_000 || 0;
			const input = this.aToB ? this?.tokenB : this?.tokenA;
			const inputAmount = Number(input.amount);
			return (
				Math.min(this.mintBTransferFee?.maximumFee, inputAmount * feeRate) /
				Math.pow(10, input.decimals)
			);
		},

		freezeAuthorityDetected: function () {
			const whitelist = {
				So11111111111111111111111111111111111111112: true,
				EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: true,
			};
			return (
				(!this.tokenA?.verified &&
					!whitelist[this.tokenA?.address?.toString()] &&
					this.poolDetail?.mintA?.freezeAuthority) ||
				(!this.tokenB?.verified &&
					!whitelist[this.tokenB?.address?.toString()] &&
					this.poolDetail?.mintB?.freezeAuthority)
			);
		},

		mintAuthorityDetected: function () {
			const whitelist = {
				So11111111111111111111111111111111111111112: true,
				EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: true,
			};
			return (
				(!this.tokenA?.verified &&
					!whitelist[this.tokenA?.address?.toString()] &&
					this.poolDetail?.mintA?.mintAuthority) ||
				(!this.tokenB?.verified &&
					!whitelist[this.tokenB?.address?.toString()] &&
					this.poolDetail?.mintB?.mintAuthority)
			);
		},

		minimumOutFluxHu: function () {
			return this.minimumOut / Math.pow(10, this.tokenB?.decimals);
		},

		priceImpact: function () {
			if (!this.poolDetail || this.amountIn <= 0) return 0;

			let aIn = this.amountIn;
			if (this.poolDetail.tokenAccountA.mint !== this.tokenA.address) {
				// aIn = Number(this.form.tokenB.amount) //Use output amount
				aIn = this.optimalRouteOutput;
			}

			const aTotal = Number(this.poolDetail.tokenAccountA.tokenAmount.amount);
			const bTotal = Number(this.poolDetail.tokenAccountB.tokenAmount.amount);

			let spotPrice = bTotal / aTotal;

			let kBefore = aTotal * bTotal;
			let bTotalAfter = kBefore / (aTotal + aIn);
			let effectivePrice = bTotalAfter / (aTotal + aIn);
			return ((spotPrice - effectivePrice) / spotPrice) * 100;
		},

		minimumOut: function () {
			return (
				this.expectedOutputFlux -
				this.expectedOutputFlux * (this.$store.state.settings.slippage / 100)
			);
		},
		aToB: function () {
			return this.poolDetail?.tokenAccountA?.mint === this.tokenA?.address;
		},

		poolTradeFee: function () {
			if (!this.routes || !this.routes[0]?.account) return 0;
			const route = this.routes[0]?.account;

			return route.tradeFeeNumerator / route.tradeFeeDenominator;
		},

		poolOwnerTradeFee: function () {
			if (!this.routes || !this.routes[0]?.account) return 0;
			const route = this.routes[0]?.account;

			return route.ownerTradeFeeNumerator / route.ownerTradeFeeDenominator;
		},

		totalPoolFee: function () {
			return this.poolTradeFee + this.poolOwnerTradeFee;
		},

		transactionFee: function () {
			let lamports = 5000;

			lamports += this.$store.getters["settings/priorityFee"] / 1_000_000;

			const vTip = this.$store.getters["settings/validatorTip"];
			if (vTip > 0) lamports += vTip;

			return lamports / Math.pow(10, 9);
		},

		poolAvailable: function () {
			return this.routes && this.routes.length > 0;
		},

		/**
		 * Returns the best routes output amount
		 * @returns {number|number|number}
		 */
		optimalRouteOutput: function () {
			console.log("expectedOutputFlux", this.jupRoute)
			if (!this.jupRoute) {
				return this.expectedOutputFlux;
			}

			const output = this.expectedOutputFlux;
			if (this.jupRoute && Number(this.jupRoute?.outAmount) > output) {
				return Number(this.jupRoute?.outAmount);
			}

			return output;
		},
		expectedOutputFlux: function () {
			if (!this.poolDetail) return 0;

			// Ensure inputs are numbers
			let inputAmount = this.amountIn;
			if (inputAmount <= 0) return 0;

			let poolAAmount = Number(
				this.poolDetail.tokenAccountA.tokenAmount.amount
			);
			let poolBAmount = Number(
				this.poolDetail.tokenAccountB.tokenAmount.amount
			);

			const inputFees = this.aToB
				? this.mintATransferFee
				: this.mintBTransferFee;
			const tokenAFeeRate = inputFees?.transferFeeBasisPoints / 10_000 || 0;
			if (tokenAFeeRate > 0) {
				const tokenAFee = Math.min(
					inputFees?.maximumFee,
					inputAmount * tokenAFeeRate
				);
				inputAmount = inputAmount - tokenAFee;
			}

			let output;
			//Inverse pool
			if (this.poolDetail.tokenAccountA.mint !== this.tokenA.address) {
				output = this.calculateInverseSwapOutput(
					poolAAmount,
					poolBAmount,
					inputAmount || 0
				);
			} else {
				output = this.calculateSwapOutput(
					poolAAmount,
					poolBAmount,
					inputAmount
				);
			}

			//Pool fees
			const poolRate = output * this.totalPoolFee;
			output = output - poolRate;

			const outputFees = this.aToB
				? this.mintBTransferFee
				: this.mintATransferFee;
			const tokenBFeeRate = outputFees?.transferFeeBasisPoints / 10_000 || 0;
			if (tokenBFeeRate > 0) {
				const tokenBFee = Math.min(
					outputFees?.maximumFee,
					output * tokenBFeeRate
				);
				output = output - tokenBFee;
			}

			return Math.floor(output);
		},
	},
	methods: {
		debounce: function (func, wait) {
			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => func(), wait);
		},

		onRefresh: async function () {
			this.loading = true;
			await this.refreshRoute(this.form, true).finally(() => {
				this.$toastr.s("Refreshed Route");
				this.loading = false;
			});
		},

		calculateSwapOutput: function (beforeTokenA, beforeTokenB, inputA = 0) {
			try {
				beforeTokenA = new BN(
					beforeTokenA.toLocaleString("fullwide", {useGrouping: false})
				);
				beforeTokenB = new BN(
					beforeTokenB.toLocaleString("fullwide", {useGrouping: false})
				);
				inputA = new BN(
					(inputA || 0)
						.toFixed(0)
						.toLocaleString("fullwide", {useGrouping: false})
				);
			} catch (e) {
				console.error(`calculateSwapOutput`, e);
				return 0;
			}

			// Calculate the amount of tokenB tokens after the trade
			let afterTokenB = beforeTokenB
				.mul(beforeTokenA)
				.div(beforeTokenA.add(inputA));

			// Calculate the amount of tokenB received from the trade
			let outputB = beforeTokenB.sub(afterTokenB);
			// console.log("calculateSwapOutput", outputB.toString());
			return outputB.toString();
		},

		calculateInverseSwapOutput: function (
			beforeTokenA,
			beforeTokenB,
			inputB = 0
		) {
			try {
				beforeTokenA = new BN(
					beforeTokenA.toLocaleString("fullwide", {useGrouping: false})
				);
				beforeTokenB = new BN(
					beforeTokenB.toLocaleString("fullwide", {useGrouping: false})
				);
				inputB = new BN(
					(inputB || 0)
						.toFixed(0)
						.toLocaleString("fullwide", {useGrouping: false})
				);
			} catch (e) {
				console.error(`Swap::calculateInverseSwapOutput`, e);
				return 0;
			}

			// Correct formula for inverse swap output
			let afterTokenB = beforeTokenB.add(inputB);
			let afterTokenA = beforeTokenA.mul(beforeTokenB).div(afterTokenB);

			let outputA = beforeTokenA.sub(afterTokenA);
			return outputA.toString();
		},

		refreshJupRoute: async function () {
			const data = await this.jupClient.quote(
				this.tokenA.address,
				this.tokenB.address,
				this.amountIn,
				this.$store.state.settings.slippage * 100
			);

			//TODO remove so we can show error on UI
			if (!data || data?.error) {
				this.jupRoute = null;
				this.jupLoading = false;
				return;
			}

			this.jupRoute = data;
			this.jupLoading = false;
			this.$store.commit("swap/setTokenBAmount", this.optimalRouteOutput);
		},

		onDualSelectUpdate: async function () {
			this.jupRoute = null;
			this.poolDetail = null;

			if (!this.tokenA.programID || this.tokenA.decimals === 0) {
				// console.log("onDualSelectUpdate tokenA RE-FETCH")
				const tokenAInfo =
					await this.$store.state.solana.client.getParsedAccountInfo(
						new web3.PublicKey(this.tokenA.address)
					);
				this.tokenA.programID = tokenAInfo?.value?.owner.toString();
				this.tokenA.decimals = tokenAInfo?.value?.data?.parsed?.info.decimals;
			}

			if (!this.tokenB.programID || this.tokenB.decimals === 0) {
				// console.log("onDualSelectUpdate tokenB RE-FETCH")
				const tokenBInfo =
					await this.$store.state.solana.client.getParsedAccountInfo(
						new web3.PublicKey(this.tokenB.address)
					);
				this.tokenB.programID = tokenBInfo?.value?.owner.toString();
				this.tokenB.decimals = tokenBInfo?.value?.data?.parsed?.info.decimals;
			}

			return this.refreshRoute();
		},

		onInputA: async function () {
			await this._refreshRoute();
			// console.log("onInputA::setTokenBAmount", this.optimalRouteOutput);
			this.$store.commit("swap/setTokenBAmount", this.optimalRouteOutput);
		},

		refreshRoute: async function () {
			await this._refreshRoute(true);
			// console.log("refreshRoute::setTokenBAmount", this.optimalRouteOutput);
			this.$store.commit("swap/setTokenBAmount", this.optimalRouteOutput);
		},
		_refreshRoute: async function (refreshPool = false) {
			// console.log("_refreshRoute", {
			// 	tokenA: this.tokenA,
			// 	tokenB: this.tokenB,
			// 	amountIn: this.amountIn,
			// })

			if (!this.tokenA || !this.tokenB) return; //No tokens set

			if (this.amountIn > 0) {
				this.jupLoading = true;
				this.jupRoute = null;
				this.debounce(() => {
					this.refreshJupRoute(this.tokenA, this.tokenB, this.amountIn);
				}, 600);
			}

			if (this.poolLoading) return;

			this.poolLoading = true;
			const tokenAPK = new web3.PublicKey(this.tokenA.address);
			const tokenBPK = new web3.PublicKey(this.tokenB.address);

			if (!this.poolDetail) {
				await this.fetchPoolDetail(tokenAPK, tokenBPK).catch(() => {
					this.poolLoading = false
				});
				this.poolLoading = false;
				return;
			}

			if (this.routes && this.routes[0]) {
				const r = this.routes[0].account;
				const aMatch = r.mintA.equals(tokenAPK) || r.mintB.equals(tokenAPK);
				const bMatch = r.mintA.equals(tokenBPK) || r.mintB.equals(tokenBPK);
				if (aMatch && bMatch && !refreshPool) {
					this.poolLoading = false;
					return; //We have the pool already
				}
			}

			await this.fetchPoolDetail(tokenAPK, tokenBPK).catch(() => {
				this.poolLoading = false
			});
			this.poolLoading = false;
		},

		fetchPoolDetail: async function (tokenAPK, tokenBPK) {
			const r = await this.$store.state.solana.fluxClient.findPools(tokenAPK, tokenBPK) || []


			if (r.length === 0) {
				console.warn(
					"no pools found for",
					tokenAPK.toString(),
					tokenBPK.toString()
				);
				//No pools found
				this.routes = [];
				this.poolDetail = null;
				this.poolLoading = false;
				return;
			}

			let top = 0;
			let useRoute = null;
			let usePD = null;

			const allowedMint = {
				CKfatsPMUf8SkiURsDXs7eK6GWb4Jsd6UDbs7twMCWxo: true,
				So11111111111111111111111111111111111111112: true,
			};

			const walletAddr = this.$store.state.wallet.address
				? new web3.PublicKey(this.$store.state.wallet.address)
				: web3.PublicKey.default;

			//TODO inefficient
			for (let i = 0; i < r.length; i++) {
				if (
					allowedMint[r[i].mintA.toString()] &&
					allowedMint[r[i].mintB.toString()]
				) {
					if (
						r[i].address !==
						"Ebbpz3PWLaQxj2oyK967RgEPbcPypjQCoZ3tpB4fwLsk"
					)
						continue;
				}


				const pool = await this.$store.state.solana.swapClient.getPool(new web3.PublicKey(r[i].address));

				const pd = await this.$store.state.solana.swapClient.getPoolDetail(
					pool.pubkey,
					pool.account,
					walletAddr
				);
				const liq =
					pd.tokenAccountA?.tokenAmount?.uiAmount +
					pd.tokenAccountB?.tokenAmount?.uiAmount;
				if (liq > top) {
					top = liq;
					useRoute = pool;
					usePD = pd;
				}
			}

			if (!useRoute) {
				this.routes = []
				this.poolDetail = null
				throw new Error("no routes found")
			}

			// console.log("Using pool", useRoute.pubkey.toString())
			this.routes = [useRoute];
			this.poolDetail = usePD
		},

		doSwap: async function () {
			if (
				!this.useJupRoute &&
				!this.acceptedRisk &&
				(this.priceImpact > 10 || this.poolTotalLiquidity < 3000)
			) {
				this.showRisk = true;
				window.gtag("event", "swap_risk_warning_shown", {
					priceImpact: this.priceImpact,
					poolLiquidity: this.poolTotalLiquidity,
					tokenA: this.tokenA.address.toString(),
					tokenB: this.tokenB.address.toString()
				});
				return;
			}
			return this.doSwapRiskAccept();
		},

		doSwapRiskAccept: async function () {
			this.showRisk = false;
			this.acceptedRisk = false;

			// Track swap initiated
			window.gtag("event", "swap_initiated", {
				route: this.useJupRoute ? "jupiter" : "fluxbeam",
				tokenA: this.tokenA.address.toString(),
				tokenB: this.tokenB.address.toString(),
				amountIn: this.amountIn,
				expectedOutput: this.optimalRouteOutput
			});

			// console.log("Swapping", this.routes);
			this.loading = true;
			await this._doSwap().catch((e) => {
				console.error("Swap failed", e);
				this.$toastr.e(parseCustomError(e.message || e), "Swap failed");
				window.gtag("event", "swap_fail", {
					error: e,
					route: this.useJupRoute ? "jupiter" : "fluxbeam",
					tokenA: this.tokenA.address.toString(),
					tokenB: this.tokenB.address.toString(),
					amountIn: this.amountIn
				});
			});
			this.loading = false;
			setTimeout(() => {
				this.$store.dispatch("cache/refreshUserBalance");
			}, 3000);
		},
		_doSwap: async function () {
			console.log("_doSwap", this.walletStore)

			if (this.walletStore?.isBotWallet) {
				const id = Math.random()
				store.commit("signatures/add_signature", {
					sig: id,
					status: {},
				});
				const resp = await createFluxBotSwap({
					wallet: this.walletStore?.address.toString(),
					inMint: this.tokenA.address,
					outMint: this.tokenB.address,
					inAmount: this.amountIn / Math.pow(10, this.tokenA.decimals),
				}).catch(e => {
					store.commit("signatures/clear_status", id)
					throw e
				});

				//Pass to txn polling
				if (resp.signature) {
					store.commit("signatures/update_status", {
						pendingSig: id,
						sig: resp.signature,
						status: {},
					});

					// Track swap success for bot wallet
					window.gtag("event", "swap_success", {
						signature: resp.signature,
						tokenA: this.tokenA.address.toString(),
						tokenB: this.tokenB.address.toString(),
						amountIn: this.amountIn,
						method: "fluxbot"
					});
					return;
				}
			}


			if (this.useJupRoute) {
				// console.log("_doSwap JUPITER ROUTING");
				const txn = await this.jupClient
					.swap(
						new web3.PublicKey(this.$store.state.wallet.address),
						this.jupRoute,
						this.$store.getters["settings/priorityFee"]
					)
					.catch((e) => {
						console.error("jupiter swap error", e);
						window.gtag("event", "swap_jupiter_error", {
							error: e,
							tokenA: this.tokenA.address.toString(),
							tokenB: this.tokenB.address.toString()
						});
						throw new Error("Jupiter error");
					});
				// console.log("JupSwap", txn);

				await this.signAndSendTransaction(txn, "swap_jup", [], () => {
					this.refreshRoute();
					window.gtag("event", "swap_success", {
						tokenA: this.tokenA.address.toString(),
						tokenB: this.tokenB.address.toString(),
						amountIn: this.amountIn,
						method: "jupiter"
					});
				});

				return;
			}

			// console.log("_doSwap FLUXBEAM ROUTING");
			const route = this.routes[0];
			if (this.amountIn <= 0) throw new Error("Invalid input amount");

			const swapTxn =
				await this.$store.state.solana.swapClient.createSwapTransaction(
					new web3.PublicKey(this.$store.state.wallet.address),
					route.pubkey,
					new TokenInput(
						new web3.PublicKey(this.tokenA.address),
						this.amountIn,
						new web3.PublicKey(
							this.tokenA.programID ||
							"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
						)
					),
					new TokenInput(
						new web3.PublicKey(this.tokenB.address),
						0,
						new web3.PublicKey(
							this.tokenB.programID ||
							"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
						)
					),
					route.account,
					Math.floor(this.amountIn),
					Math.floor(this.minimumOut)
				);

			const unitLimit = 160_000;
			const unitPrice = Math.floor(
				this.$store.getters["settings/priorityFee"] / unitLimit
			);

			const txn = new web3.Transaction();
			txn.add(
				web3.ComputeBudgetProgram.setComputeUnitLimit({units: unitLimit}),
				web3.ComputeBudgetProgram.setComputeUnitPrice({
					microLamports: unitPrice,
				})
			);
			txn.add(swapTxn);

			await this.signAndSendTransaction(txn, "swap", [], () => {
				this.refreshRoute();
				window.gtag("event", "swap_success", {
					tokenA: this.tokenA.address.toString(),
					tokenB: this.tokenB.address.toString(),
					amountIn: this.amountIn,
					method: "fluxbeam"
				});
			});
		},
	},
	beforeMount() {
		this.jupClient = new JupiterClient(this.$store.state.solana.client);
	},
	mounted() {
		this.interval = setInterval(() => {
			this.refreshRoute();
		}, 30_000);
		this.refreshRoute();
	},
	beforeDestroy() {
		clearInterval(this.interval);
	},
};
</script>

<style scoped>
.settings-link {
	display: flex;
	align-items: center;
}

.refresh-btn * {
	transition: all 0.3s linear;
}

.refresh-btn:active * {
	transform: rotate(359deg);
}

.xsmall td {
	font-size: .75em;
}
</style>
